// User types
export enum UserRole {
  ADMIN = 'admin',
  DEVELOPER = 'developer',
  VIEWER = 'viewer',
}

export interface User {
  id: string
  email: string
  full_name: string
  role: UserRole
  avatar_url?: string
  is_active: string
  last_login?: string
  created_at: string
  updated_at: string
}

export interface AuthResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  user: User
}

export interface GoogleAuthUrlResponse {
  authorization_url: string
  state: string
}

// Workspace types
export interface Workspace {
  id: string
  name: string
  description?: string
  owner_id: string
  created_at: string
  updated_at: string
}

export interface WorkspaceCreate {
  name: string
  description?: string
}

// Agent types
export enum AgentFramework {
  LANGCHAIN = 'langchain',
  CREWAI = 'crewai',
  ADK = 'adk',
  CUSTOM = 'custom',
}

export enum AgentStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DEPLOYING = 'deploying',
  ERROR = 'error',
  ARCHIVED = 'archived',
}

export interface Agent {
  id: string
  workspace_id: string
  name: string
  description?: string
  framework: AgentFramework
  configuration: Record<string, any>
  status: AgentStatus
  version: number
  created_by: string
  created_at: string
  updated_at: string
  // Convenience properties (extracted from configuration)
  model?: string
  temperature?: number
  max_tokens?: number
  system_prompt?: string
  tools?: string[]
}

export interface AgentCreate {
  workspace_id?: string
  name: string
  description?: string
  framework: AgentFramework
  configuration?: Record<string, any>
  model?: string
  temperature?: number
  max_tokens?: number
  system_prompt?: string
  tools?: string[]
  status?: AgentStatus
}

export interface AgentUpdate {
  name?: string
  description?: string
  system_prompt?: string
  temperature?: number
  max_tokens?: number
  configuration?: Record<string, any>
  status?: AgentStatus
}

// Workflow types
export enum WorkflowStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export interface WorkflowNode {
  id: string
  type: string
  data: Record<string, any>
  position: { x: number; y: number }
}

export interface WorkflowEdge {
  id: string
  source: string
  target: string
  type?: string
}

export interface Workflow {
  id: string
  workspace_id: string
  name: string
  description?: string
  definition: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
  }
  status: WorkflowStatus
  created_by: string
  created_at: string
  updated_at: string
}

export interface WorkflowCreate {
  workspace_id?: string
  name: string
  description?: string
  framework: string
  status?: WorkflowStatus
  definition: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
  }
}

// Execution types
export type ExecutionMode = 'local' | 'hpc'

export interface HPCResourceConfig {
  cluster_id: string
  nodes?: number
  cpus_per_node?: number
  gpus?: number
  gpu_type?: string
  memory_gb?: number
  walltime_hours?: number
  queue?: string
}

export interface HPCTrainingConfig {
  job_type: 'rl_training' | 'llm_finetuning' | 'multi_agent_simulation' | 'batch_inference'
  total_epochs?: number
  batch_size?: number
  learning_rate?: number
  checkpoint_enabled?: boolean
  checkpoint_interval_minutes?: number
  auto_resume?: boolean
  training_parameters?: Record<string, any>
}

export interface Execution {
  id: string
  agent_id: string
  input: Record<string, any>
  output?: Record<string, any>
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  error_message?: string
  started_at?: string
  completed_at?: string
  duration_ms?: number
  token_usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
  cost?: number
  execution_mode?: ExecutionMode
  hpc_job_id?: string
  hpc_status?: string
  hpc_progress?: number
  created_at: string
  updated_at: string
  // Convenience properties
  duration?: number  // duration_ms converted to seconds
  error?: string  // alias for error_message
}

export interface ExecutionCreate {
  agent_id: string
  input_data?: Record<string, any>
  input?: Record<string, any>  // Alternative name
  async_execution?: boolean
  execution_mode?: ExecutionMode
  hpc_resources?: HPCResourceConfig
  hpc_training?: HPCTrainingConfig
}

export interface ExecutionStats {
  total_executions: number
  completed: number
  failed: number
  success_rate: number
  average_duration_ms: number
  total_cost: number
}

// MCP types
export interface MCPServer {
  id: string
  name: string
  type: 'http' | 'websocket' | 'stdio' | 'sse'
  url: string
  is_active: boolean
  tools_count: number
  metadata?: Record<string, any>
}

export interface MCPServerCreate {
  id: string
  name: string
  type: string
  url: string
  api_key?: string
  metadata?: Record<string, any>
}

export interface MCPTool {
  name: string
  description: string
  parameters?: Record<string, any>
}

export interface MCPToolCall {
  server_id: string
  tool_name: string
  arguments: Record<string, any>
  timeout?: number
}

// Knowledge Base / Vector DB types
export interface Collection {
  name: string
  vectors_count?: number
  points_count?: number
  status?: string
  config?: {
    vector_size: number
    distance: string
  }
}

export interface CollectionCreate {
  name: string
  vector_size?: number
  distance?: string
  metadata?: Record<string, any>
}

export interface Document {
  id?: string
  text: string
  metadata?: Record<string, any>
}

export interface SearchResult {
  id: string
  score: number
  text: string
  metadata?: Record<string, any>
}

export interface RAGRequest {
  collection_name: string
  query: string
  num_contexts?: number
  score_threshold?: number
}

export interface RAGResponse {
  query: string
  num_contexts: number
  contexts: string[]
  combined_context: string
  sources: Array<{
    id: string
    score: number
    metadata?: Record<string, any>
  }>
}

// Pagination
export interface PaginatedResponse<T> {
  total: number
  items: T[]
  skip: number
  limit: number
}

// Team types
export enum TeamCoordinationStrategy {
  SEQUENTIAL = 'SEQUENTIAL',
  PARALLEL = 'PARALLEL',
  HIERARCHICAL = 'HIERARCHICAL',
  CONSENSUS = 'CONSENSUS',
  FEEDBACK_LOOP = 'FEEDBACK_LOOP',
}

export enum TeamStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum AgentRole {
  COORDINATOR = 'COORDINATOR',
  RESEARCHER = 'RESEARCHER',
  ANALYST = 'ANALYST',
  WRITER = 'WRITER',
  REVIEWER = 'REVIEWER',
  VALIDATOR = 'VALIDATOR',
  TESTER = 'TESTER',
  DEBUGGER = 'DEBUGGER',
  OPTIMIZER = 'OPTIMIZER',
  SPECIALIST = 'SPECIALIST',
  CUSTOM = 'CUSTOM',
}

export interface TeamMember {
  id: string
  team_id: string
  agent_id: string
  role: AgentRole
  role_description?: string
  execution_order: number
  is_required: boolean
  can_delegate: boolean
  feedback_sources: string[]
  feedback_targets: string[]
  configuration: Record<string, any>
  created_at: string
  updated_at: string
}

export interface TeamMemberCreate {
  agent_id: string
  role: AgentRole
  role_description?: string
  execution_order?: number
  is_required?: boolean
  can_delegate?: boolean
  feedback_sources?: string[]
  feedback_targets?: string[]
  configuration?: Record<string, any>
}

export interface AgentTeam {
  id: string
  workspace_id: string
  name: string
  description?: string
  coordinator_agent_id?: string
  strategy: TeamCoordinationStrategy
  configuration: Record<string, any>
  feedback_enabled: boolean
  max_iterations?: number
  status: TeamStatus
  created_by: string
  created_at: string
  updated_at: string
  members: TeamMember[]
}

export interface AgentTeamCreate {
  workspace_id?: string
  name: string
  description?: string
  coordinator_agent_id?: string
  strategy: TeamCoordinationStrategy
  configuration?: Record<string, any>
  feedback_enabled?: boolean
  max_iterations?: number
  members?: TeamMemberCreate[]
}

export interface AgentTeamUpdate {
  name?: string
  description?: string
  coordinator_agent_id?: string
  strategy?: TeamCoordinationStrategy
  configuration?: Record<string, any>
  feedback_enabled?: boolean
  max_iterations?: number
  status?: TeamStatus
}

export enum TeamExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface TeamExecution {
  id: string
  team_id: string
  status: TeamExecutionStatus
  input_data: Record<string, any>
  output_data?: Record<string, any>
  error?: string
  duration_ms?: number
  total_tokens: number
  total_cost: number
  iterations_completed: number
  max_iterations_reached: boolean
  execution_metadata: Record<string, any>
  created_at: string
  updated_at: string
}

export interface TeamExecutionCreate {
  team_id: string
  input_data: Record<string, any>
}

export interface AgentMessage {
  id: string
  team_execution_id?: string
  from_agent_id: string
  to_agent_id: string
  message_type: string
  content: Record<string, any>
  iteration: number
  parent_message_id?: string
  created_at: string
  updated_at: string
}

// Knowledge Graph types
export enum GraphType {
  ONTOLOGY = 'ONTOLOGY',
  SEMANTIC = 'SEMANTIC',
  CAUSAL = 'CAUSAL',
  TEMPORAL = 'TEMPORAL',
  HIERARCHICAL = 'HIERARCHICAL',
  NETWORK = 'NETWORK',
  WORKFLOW = 'WORKFLOW',
  AGENT_MEMORY = 'AGENT_MEMORY',
}

export enum GraphStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum NodeType {
  CONCEPT = 'CONCEPT',
  ENTITY = 'ENTITY',
  EVENT = 'EVENT',
  ATTRIBUTE = 'ATTRIBUTE',
  DOCUMENT = 'DOCUMENT',
  AGENT = 'AGENT',
  TASK = 'TASK',
  RULE = 'RULE',
  CUSTOM = 'CUSTOM',
}

export enum RelationType {
  IS_A = 'IS_A',
  HAS_A = 'HAS_A',
  PART_OF = 'PART_OF',
  RELATED_TO = 'RELATED_TO',
  DEPENDS_ON = 'DEPENDS_ON',
  CAUSES = 'CAUSES',
  LEADS_TO = 'LEADS_TO',
  SIMILAR_TO = 'SIMILAR_TO',
  OPPOSITE_OF = 'OPPOSITE_OF',
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  CREATED_BY = 'CREATED_BY',
  USED_BY = 'USED_BY',
  CUSTOM = 'CUSTOM',
}

export interface KnowledgeGraph {
  id: string
  name: string
  description?: string
  graph_type: GraphType
  status: GraphStatus
  neo4j_graph_id?: string
  workspace_id: string
  created_by: string
  node_count: number
  edge_count: number
  configuration: Record<string, any>
  tags: string[]
  created_at: string
  updated_at: string
}

export interface KnowledgeGraphCreate {
  name: string
  description?: string
  graph_type: GraphType
  status?: GraphStatus
  workspace_id: string
  configuration?: Record<string, any>
  tags?: string[]
}

export interface KnowledgeGraphUpdate {
  name?: string
  description?: string
  status?: GraphStatus
  configuration?: Record<string, any>
  tags?: string[]
}

export interface GraphNode {
  id: string
  node_type: NodeType
  label: string
  properties: Record<string, any>
  metadata: Record<string, any>
  created_at?: string
  updated_at?: string
}

export interface GraphNodeCreate {
  node_type: NodeType
  label: string
  properties?: Record<string, any>
  metadata?: Record<string, any>
}

export interface GraphNodeUpdate {
  label?: string
  properties?: Record<string, any>
  metadata?: Record<string, any>
}

export interface GraphRelationship {
  id: string
  source_node_id: string
  target_node_id: string
  relation_type: RelationType
  label?: string
  properties: Record<string, any>
  weight?: number
}

export interface GraphRelationshipCreate {
  source_node_id: string
  target_node_id: string
  relation_type: RelationType
  label?: string
  properties?: Record<string, any>
  weight?: number
}

export interface GraphSearchRequest {
  query: string
  node_types?: NodeType[]
  limit?: number
  properties?: Record<string, any>
}

export interface GraphSearchResponse {
  nodes: GraphNode[]
  total: number
}

export interface GraphPathRequest {
  source_node_id: string
  target_node_id: string
  max_depth?: number
  relation_types?: RelationType[]
}

export interface GraphPathResponse {
  paths: any[]
  shortest_path_length?: number
  total_paths: number
}

export interface GraphVisualizationNode {
  id: string
  label: string
  node_type: NodeType
  properties: Record<string, any>
  x?: number
  y?: number
}

export interface GraphVisualizationEdge {
  id: string
  source: string
  target: string
  relation_type: RelationType
  label?: string
  weight?: number
}

export interface GraphVisualizationData {
  nodes: GraphVisualizationNode[]
  edges: GraphVisualizationEdge[]
  total_nodes: number
  total_edges: number
  layout_type: string
}

export interface GraphStatistics {
  total_nodes: number
  total_relationships: number
  node_type_counts: Record<string, number>
  relationship_type_counts: Record<string, number>
  avg_degree: number
  density: number
  connected_components: number
}

export interface AgentGraphAssociation {
  id: string
  agent_id: string
  knowledge_graph_id: string
  can_read: boolean
  can_write: boolean
  context: Record<string, any>
  created_at: string
}

export interface TeamGraphAssociation {
  id: string
  team_id: string
  knowledge_graph_id: string
  can_read: boolean
  can_write: boolean
  context: Record<string, any>
  created_at: string
}

// HPC types
export enum SchedulerType {
  PBS = 'pbs',
  SLURM = 'slurm',
  LSF = 'lsf',
  SGE = 'sge',
}

export enum ClusterStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  MAINTENANCE = 'maintenance',
  ERROR = 'error',
}

export enum HPCJobType {
  RL_TRAINING = 'rl_training',
  LLM_FINETUNING = 'llm_finetuning',
  MULTI_AGENT_SIMULATION = 'multi_agent_simulation',
  BATCH_INFERENCE = 'batch_inference',
  CUSTOM = 'custom',
}

export enum HPCJobStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  QUEUED = 'queued',
  ALLOCATING = 'allocating',
  RUNNING = 'running',
  CHECKPOINTING = 'checkpointing',
  PAUSED = 'paused',
  RESUMING = 'resuming',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum ArtifactType {
  MODEL = 'model',
  CHECKPOINT = 'checkpoint',
  LOGS = 'logs',
  METRICS = 'metrics',
  DATASET = 'dataset',
  VISUALIZATION = 'visualization',
  CONFIG = 'config',
  CODE = 'code',
  REPORT = 'report',
  OTHER = 'other',
}

export interface ResourceSpec {
  nodes: number
  cpus_per_node: number
  gpus: number
  gpu_type?: string
  memory_gb: number
  walltime_seconds: number
}

export interface HPCCluster {
  id: string
  name: string
  description?: string
  hostname: string
  port: number
  scheduler_type: SchedulerType
  workspace_id: string
  ssh_username: string
  has_ssh_key: boolean
  max_concurrent_jobs: number
  default_queue: string
  available_queues: string[]
  gpu_types: string[]
  work_directory?: string
  scratch_directory?: string
  status: ClusterStatus
  last_health_check?: string
  health_check_message?: string
  environment_modules: string[]
  configuration: Record<string, any>
  created_at: string
  updated_at: string
  active_jobs_count: number
}

export interface HPCClusterCreate {
  name: string
  description?: string
  hostname: string
  port?: number
  scheduler_type?: SchedulerType
  ssh_username: string
  ssh_key_path?: string
  ssh_private_key?: string
  max_concurrent_jobs?: number
  default_queue?: string
  available_queues?: string[]
  gpu_types?: string[]
  work_directory?: string
  scratch_directory?: string
  environment_modules?: string[]
  configuration?: Record<string, any>
}

export interface HPCClusterUpdate {
  name?: string
  description?: string
  hostname?: string
  port?: number
  scheduler_type?: SchedulerType
  ssh_username?: string
  ssh_key_path?: string
  ssh_private_key?: string
  max_concurrent_jobs?: number
  default_queue?: string
  available_queues?: string[]
  gpu_types?: string[]
  work_directory?: string
  scratch_directory?: string
  environment_modules?: string[]
  configuration?: Record<string, any>
  status?: ClusterStatus
}

export interface HPCJob {
  id: string
  cluster_id: string
  user_id: string
  workspace_id: string
  execution_id?: string
  team_execution_id?: string
  pbs_job_id?: string
  job_name: string
  job_type: HPCJobType
  description?: string
  status: HPCJobStatus
  status_message?: string
  resources: ResourceSpec
  queue: string
  priority?: string
  training_parameters: Record<string, any>
  config_file: Record<string, any>
  has_training_code: boolean
  checkpoint_enabled: boolean
  checkpoint_interval_minutes: number
  auto_resume: boolean
  progress_percent: number
  current_epoch?: number
  total_epochs?: number
  current_step?: number
  total_steps?: number
  metrics: Record<string, any>
  resource_usage: Record<string, any>
  submitted_at?: string
  queued_at?: string
  started_at?: string
  completed_at?: string
  estimated_completion?: string
  queue_position?: number
  estimated_wait_minutes?: number
  error_message?: string
  exit_code?: number
  node_hours_requested?: number
  node_hours_used: number
  estimated_cost?: number
  actual_cost?: number
  created_at: string
  updated_at: string
  checkpoints_count: number
  artifacts_count: number
}

export interface HPCJobCreate {
  cluster_id: string
  job_name: string
  job_type: HPCJobType
  description?: string
  resources?: ResourceSpec
  queue?: string
  training_parameters?: Record<string, any>
  config_file?: Record<string, any>
  training_code?: string
  environment_vars?: Record<string, string>
  input_files?: string[]
  output_files?: string[]
  checkpoint_enabled?: boolean
  checkpoint_interval_minutes?: number
  auto_resume?: boolean
  submit_immediately?: boolean
}

export interface HPCJobUpdate {
  job_name?: string
  description?: string
  resources?: ResourceSpec
  queue?: string
  training_parameters?: Record<string, any>
  config_file?: Record<string, any>
  training_code?: string
  environment_vars?: Record<string, string>
  checkpoint_enabled?: boolean
  checkpoint_interval_minutes?: number
}

export interface HPCJobSummary {
  id: string
  job_name: string
  job_type: HPCJobType
  status: HPCJobStatus
  progress_percent: number
  cluster_name: string
  created_at: string
  submitted_at?: string
  completed_at?: string
}

export interface HPCJobMetrics {
  job_id: string
  timestamp: string
  progress_percent: number
  current_epoch?: number
  current_step?: number
  metrics: Record<string, any>
  resource_usage: Record<string, any>
}

export interface HPCCheckpoint {
  id: string
  job_id: string
  checkpoint_number: number
  checkpoint_name?: string
  checkpoint_path: string
  is_auto: boolean
  epoch?: number
  step?: number
  global_step?: number
  metrics_snapshot: Record<string, any>
  parameters_snapshot: Record<string, any>
  size_bytes?: number
  size_mb: number
  file_count?: number
  checkpoint_type?: string
  is_valid: boolean
  validation_message?: string
  downloaded: boolean
  downloaded_at?: string
  local_path?: string
  created_at: string
}

export interface HPCArtifact {
  id: string
  job_id: string
  artifact_type: ArtifactType
  name: string
  description?: string
  remote_path: string
  is_directory: boolean
  size_bytes?: number
  size_human: string
  file_count?: number
  metadata: Record<string, any>
  content_hash?: string
  downloaded: boolean
  downloaded_at?: string
  local_path?: string
  download_count: number
  expires_at?: string
  is_expired: boolean
  created_at: string
}

export interface ClusterHealthResponse {
  cluster_id: string
  success: boolean
  message: string
  timestamp: string
}

export interface ClusterQueueInfo {
  queue_name: string
  state: string
  max_running?: number
  total_jobs?: number
  running_jobs?: number
}

export interface HPCJobList {
  items: HPCJobSummary[]
  total: number
  page: number
  page_size: number
  pages: number
}

// HPC WebSocket event types
export interface HPCJobUpdateEvent {
  type: 'hpc.job_update'
  job_id: string
  job_name: string
  job_type: HPCJobType
  status: HPCJobStatus
  progress_percent: number
  current_epoch?: number
  current_step?: number
  pbs_job_id?: string
  queue_position?: number
  estimated_wait_minutes?: number
  error_message?: string
  timestamp: string
}

export interface HPCMetricsUpdateEvent {
  type: 'hpc.metrics_update'
  job_id: string
  job_name: string
  progress_percent: number
  current_epoch?: number
  current_step?: number
  metrics: Record<string, any>
  resource_usage: Record<string, any>
  timestamp: string
}

export interface HPCCheckpointCreatedEvent {
  type: 'hpc.checkpoint_created'
  job_id: string
  job_name: string
  checkpoint_id: string
  checkpoint_number: number
  checkpoint_name?: string
  epoch?: number
  step?: number
  is_auto: boolean
  size_bytes?: number
  timestamp: string
}

export interface HPCArtifactsFoundEvent {
  type: 'hpc.artifacts_found'
  job_id: string
  job_name: string
  artifacts: Array<{
    id: string
    name: string
    artifact_type: ArtifactType
    size_bytes?: number
    remote_path: string
  }>
  artifact_count: number
  timestamp: string
}

export interface HPCClusterStatusEvent {
  type: 'hpc.cluster_status'
  cluster_id: string
  cluster_name: string
  status: ClusterStatus
  last_health_check?: string
  health_check_message?: string
  timestamp: string
}

// Tool types
export interface ToolParameter {
  type: string
  description?: string
  required?: boolean
  default?: any
}

export interface ToolInfo {
  name: string
  description: string
  category: string
  server_type: string
  parameters: Record<string, ToolParameter>
  requires_auth?: boolean
}

export interface ToolListResponse {
  tools: ToolInfo[]
  total: number
  categories: string[]
}

export interface ToolExecutionRequest {
  tool_name: string
  params: Record<string, any>
  agent_id?: string
  priority?: 'low' | 'normal' | 'high' | 'critical'
}

export interface ToolExecutionResponse {
  success: boolean
  tool_name: string
  result?: any
  error?: string
  execution_time_ms: number
  cached?: boolean
}

export interface AgentToolConfig {
  agent_id: string
  enabled_tools: string[]
  tool_categories: string[]
  custom_config: Record<string, any>
}

export interface ToolStatsResponse {
  total_calls: number
  successful_calls: number
  failed_calls: number
  average_execution_time_ms: number
  tools_used: Record<string, number>
}

// API Error
export interface APIError {
  detail: string
}

// ==================== Intelligent Execution Types ====================

export interface IntelligentTaskInfo {
  id: string
  description: string
  specialist_type: string
  complexity: number
  dependencies: string[]
}

export interface IntelligentAnalyzeRequest {
  prompt: string
}

export interface IntelligentAnalyzeResponse {
  original_prompt: string
  tasks: IntelligentTaskInfo[]
  parallel_groups: string[][]
  total_complexity: number
  estimated_cost: number
}

export interface IntelligentExecuteRequest {
  prompt: string
  quality_threshold?: number
  max_revisions?: number
  workspace_id?: string
}

export interface IntelligentTaskResult {
  specialist: string
  quality_score: number | null
  revision_count: number
  result_preview: string
}

export interface IntelligentPlanInfo {
  tasks: number
  parallel_groups: string[][]
  complexity: number
}

export interface IntelligentExecuteResponse {
  execution_id: string
  status: string
  final_output: string
  task_results: Record<string, IntelligentTaskResult>
  plan: IntelligentPlanInfo
  quality_score: number
  total_cost: number
}

export interface IntelligentProviderStatus {
  local: boolean
  openai: boolean
  anthropic: boolean
  google: boolean
}

export interface IntelligentSpecialistInfo {
  type: string
  description: string
}

export interface IntelligentConfig {
  enabled: boolean
  ai_task_breakdown: boolean
  ai_quality_review: boolean
  parallel_detection: boolean
  default_quality_threshold: number
  max_revision_iterations: number
  prefer_local_llm: boolean
  auto_fallback_to_cloud: boolean
}

// ==================== Crew Types (CrewAI Multi-Agent Teams) ====================

export interface CrewAgent {
  name: string
  role: string
}

export interface CrewCapabilities {
  operations: string[]
  input_types: string[]
  output_types: string[]
  integrations: string[]
  max_context_tokens: number
  supports_streaming: boolean
  supports_hitl: boolean
  process_types?: string[]
  member_count?: number
  supports_delegation?: boolean
}

export interface Crew {
  name: string
  display_name: string
  description: string
  domain: string
  agents: CrewAgent[]
  enabled: boolean
  version: string
  process_type: 'sequential' | 'parallel' | 'hierarchical'
  capabilities?: CrewCapabilities
}

export interface CrewInvokeRequest {
  prompt: string
  context: Record<string, any>
}

export interface CrewInvokeResponse {
  success: boolean
  content?: string
  summary?: string
  findings?: any[]
  recommendations?: any[]
  confidence?: number
  error?: string
  execution_mode?: string
  metadata: {
    crew: string
    request_id: string
    token_count?: number
    model?: string
    quality_score?: number
    audit?: {
      merkle_proof?: string
      event_count?: number
    }
    telemetry?: {
      total_input_tokens?: number
      total_output_tokens?: number
      total_tokens?: number
      total_cost_usd?: number
      duration_ms?: number
      llm_call_count?: number
      model_used?: string
      provider_used?: string
      cost_breakdown?: Record<string, number>
    }
    agents_involved?: string[]
  }
}

// ==================== LangGraph Visualization Types ====================

export type GraphNodeStatus = 'pending' | 'running' | 'completed' | 'error' | 'skipped'

export interface GraphNodeMetrics {
  tokens: number
  duration_ms: number
  cost: number
}

export interface GraphNode {
  id: string
  type: string
  label: string
  description: string
  status: GraphNodeStatus
  position: { x: number; y: number }
  metrics?: GraphNodeMetrics
}

export interface GraphEdge {
  id: string
  source: string
  target: string
  label?: string
  condition?: string
}

export interface AgentGraphResponse {
  agent_name: string
  display_name: string
  version: string
  domain: string
  nodes: GraphNode[]
  edges: GraphEdge[]
  metadata: {
    quality_threshold: number
    max_revisions: number
    enable_quality_review: boolean
    enable_memory_persistence: boolean
    enable_audit_trail: boolean
    memory_layers: string[]
    capabilities?: Record<string, any>
  }
}

export interface AgentGraphExecutionResponse {
  agent_name: string
  execution_id: string
  execution_state?: {
    status: 'running' | 'completed' | 'error'
    started_at?: string
    completed_at?: string
    total_tokens?: number
    total_cost_usd?: number
    duration_ms?: number
    quality_score?: number
    error?: string
  }
  current_node?: string
  nodes: Array<{
    id: string
    status: GraphNodeStatus
    metrics?: GraphNodeMetrics
  }>
}

export interface CrewGraphResponse {
  crew_name: string
  display_name: string
  version: string
  process_type: 'sequential' | 'parallel' | 'hierarchical'
  nodes: GraphNode[]
  edges: GraphEdge[]
  metadata: {
    domain: string
    agent_count: number
    agents: Array<{ name: string; role: string }>
  }
}

// Legacy aliases for backwards compatibility
export type LangGraphNode = GraphNode
export type LangGraphEdge = GraphEdge
export interface LangGraphState {
  nodes: GraphNode[]
  edges: GraphEdge[]
  current_node?: string
  execution_path?: string[]
}

// ==================== Tag Types for Filtering ====================

// Agent category tags for filtering system agents
export type AgentTag =
  | 'explorer'      // Exploration/discovery agents
  | 'orchestrator'  // Multi-agent coordination
  | 'planner'       // Planning/architecture
  | 'builder'       // Construction/creation
  | 'verifier'      // Validation/QA
  | 'knowledge'     // Knowledge retrieval
  | 'reporting'     // Report generation
  | 'analysis'      // Analysis tasks
  | 'observability' // Monitoring/health
  | 'automation'    // Workflow automation

// Workflow category tags
export type WorkflowTag =
  | 'n8n'           // Deployed to n8n service
  | 'langgraph'     // LangGraph-based workflows
  | 'crewai'        // CrewAI-based workflows
  | 'adk'           // ADK-based workflows
  | 'external'      // External/3rd-party integrations
  | 'internal'      // Internal Ziro operations
  | 'scheduled'     // Scheduled/cron workflows
  | 'webhook'       // Webhook-triggered
  | 'ai-generated'  // Generated by AI agents
  | 'template'      // Reusable templates

// Tag mapping for centralized agents
export const AGENT_TAGS: Record<string, AgentTag[]> = {
  'knowledge': ['knowledge', 'explorer'],
  'reporting': ['reporting', 'analysis'],
  'n8n-workflow-builder': ['orchestrator', 'automation', 'builder'],
  'n8n-enhanced-workflow-builder': ['orchestrator', 'automation', 'planner', 'verifier'],
  'n8n-explorer': ['explorer', 'automation'],
  'n8n-builder': ['builder', 'automation'],
  'n8n-planning': ['planner', 'automation'],
  'n8n-verification': ['verifier', 'automation'],
  'n8n-qa': ['verifier', 'automation'],
  'project-health': ['observability', 'analysis'],
  'risk-analysis': ['analysis', 'planner'],
  'dependency-analysis': ['analysis', 'explorer'],
  'task-breakdown': ['planner', 'builder'],
  'cost-analysis': ['analysis', 'reporting'],
  'explorer': ['explorer', 'analysis'],
}

// All available agent tags for filter UI
export const ALL_AGENT_TAGS: AgentTag[] = [
  'explorer',
  'orchestrator',
  'planner',
  'builder',
  'verifier',
  'knowledge',
  'reporting',
  'analysis',
  'observability',
  'automation',
]

// All available workflow tags for filter UI
export const ALL_WORKFLOW_TAGS: WorkflowTag[] = [
  'n8n',
  'langgraph',
  'crewai',
  'adk',
  'external',
  'internal',
  'scheduled',
  'webhook',
  'ai-generated',
  'template',
]

// Centralized agent type (from /api/v1/agents/centralized)
export interface CentralizedAgent {
  name: string
  display_name: string
  description: string
  domain: string
  enabled: boolean
  version?: string
  capabilities?: Record<string, any>
  tags?: AgentTag[]
}

// N8N workflow from the n8n service
export interface N8NWorkflow {
  id: string
  name: string
  active: boolean
  createdAt?: string
  updatedAt?: string
  nodes?: Array<{
    type: string
    name: string
    parameters?: Record<string, any>
  }>
  tags?: WorkflowTag[]
}

// ==================== Unified Execution Types (re-exports) ====================

export * from './unified'
export * from './orchestrator'
